import { useState } from 'react';
import { Box, Typography } from '@mui/material';

import { SHRED_COLORS, SHRED_COPY, SHRED_FONTS, SHRED_PODIUM_METALS } from '../constants';
import { formatAwardValue } from '../helpers';
import { LevelChip, SectionTitle } from './shared';

const faceSx = {
  position: 'absolute',
  inset: 0,
  p: 2,
  borderRadius: '14px',
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  overflow: 'hidden',
};

const AwardCard = ({ award, reduced }) => {
  const [flipped, setFlipped] = useState(false);
  const leader = award.winners[0];
  return (
    <Box sx={{ perspective: '1000px', height: 272, position: 'relative' }}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          textAlign: 'left',
          transformStyle: 'preserve-3d',
          transition: reduced ? 'none' : 'transform 600ms cubic-bezier(0.2,0.8,0.2,1)',
          transform: flipped ? 'rotateY(180deg)' : 'none',
          borderRadius: '14px',
        }}
      >
        <Box
          sx={{
            ...faceSx,
            visibility: flipped ? 'hidden' : 'visible',
            transition: reduced ? 'none' : 'visibility 0s linear 300ms',
            background: `linear-gradient(160deg, ${award.accent}33 0%, ${SHRED_COLORS.panel} 65%)`,
            border: `1px solid ${award.accent}66`,
          }}
        >
          <Box sx={{ fontSize: 34, lineHeight: 1 }} aria-hidden="true">{award.icon}</Box>
          <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: 26, textTransform: 'uppercase', color: award.accent, lineHeight: 1 }}>
            {award.title}
          </Typography>
          <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 13, color: SHRED_COLORS.muted }}>{award.description}</Typography>
          <Box sx={{ mt: 'auto' }}>
            {leader ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 700, fontSize: 22, color: SHRED_COLORS.text }}>
                    {leader.username}
                  </Typography>
                  <LevelChip level={leader.level} />
                </Box>
                <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 14, color: SHRED_COLORS.gold }}>
                  {formatAwardValue(award.valueUnit, leader.value)}
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 14, color: SHRED_COLORS.muted }}>{SHRED_COPY.AWARD_EMPTY}</Typography>
            )}
            <Typography sx={{ mt: 0.5, fontFamily: SHRED_FONTS.numeric, fontSize: 10, letterSpacing: '0.14em', color: SHRED_COLORS.muted }}>
              TAP FOR TOP 3 + REWARD
            </Typography>
          </Box>
        </Box>

        <Box
          id={`shred-award-${award.id}-back`}
          sx={{
            ...faceSx,
            visibility: flipped ? 'visible' : 'hidden',
            transition: reduced ? 'none' : `visibility 0s linear ${flipped ? 0 : 300}ms`,
            transform: 'rotateY(180deg)',
            background: SHRED_COLORS.panelRaised,
            border: `1px solid ${award.accent}88`,
          }}
        >
          <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 11, letterSpacing: '0.16em', color: award.accent }}>
            {award.title.toUpperCase()} · TOP 3
          </Typography>
          {award.winners.length === 0 && (
            <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 14, color: SHRED_COLORS.muted }}>{SHRED_COPY.AWARD_EMPTY}</Typography>
          )}
          {award.winners.map((winner) => (
            <Box key={winner.rank} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: 20, width: 18, color: SHRED_PODIUM_METALS[winner.rank - 1]?.color }}>
                {winner.rank}
              </Typography>
              <Typography noWrap sx={{ fontFamily: SHRED_FONTS.body, fontWeight: 600, fontSize: 14, flex: 1, minWidth: 0 }}>
                {winner.username}
              </Typography>
              <LevelChip level={winner.level} />
              <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 12, color: SHRED_COLORS.gold }}>
                {formatAwardValue(award.valueUnit, winner.value)}
              </Typography>
            </Box>
          ))}
          <Box sx={{ mt: 'auto', pt: 1, borderTop: `1px dashed ${SHRED_COLORS.line}` }}>
            <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 10, letterSpacing: '0.14em', color: SHRED_COLORS.muted }}>{SHRED_COPY.REWARD_LABEL}</Typography>
            <Typography sx={{ fontFamily: SHRED_FONTS.body, fontWeight: 600, fontSize: 13 }}>{award.reward.title || 'Announced soon'}</Typography>
            {award.reward.items.length > 0 && (
              <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 12, color: SHRED_COLORS.muted }}>{award.reward.items.join(' · ')}</Typography>
            )}
          </Box>
        </Box>
      </Box>
      {/* Only the toggle is a button; both faces stay ordinary content so winners and rewards are readable. */}
      <Box
        component="button"
        type="button"
        onClick={() => setFlipped((value) => !value)}
        aria-expanded={flipped}
        aria-controls={`shred-award-${award.id}-back`}
        aria-label={`${award.title}: ${flipped ? 'hide' : 'show'} top three and reward`}
        sx={{
          position: 'absolute',
          inset: 0,
          p: 0,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          borderRadius: '14px',
          '&:focus-visible': { outline: `2px solid ${SHRED_COLORS.gold}`, outlineOffset: 3 },
        }}
      />
    </Box>
  );
};

const Awards = ({ awards, reduced }) => (
  <Box component="section" aria-labelledby="shred-awards-title">
    <SectionTitle kicker="Beyond rank" title={SHRED_COPY.AWARDS_TITLE} hint={SHRED_COPY.AWARDS_HINT} id="shred-awards-title" />
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 270px), 1fr))', gap: 2 }}>
      {awards.map((award) => (
        <AwardCard key={award.id} award={award} reduced={reduced} />
      ))}
    </Box>
  </Box>
);

export default Awards;
